import { parseResourceId } from "./stagingModel";

// What a staged transform row runs with (TTTL-224). The Studio keeps the
// text of a script it saved or loaded, so a run does not go back to the
// server for a file it just wrote; only a row it holds no text for — one
// staged from the Explorer without being opened — reads the stored file,
// and a failed read is reported in the Studio's words rather than the
// engine's.

/** The Output panel's line for a stored script that could not be read. */
export const SCRIPT_RELOAD_FAILED =
  "The saved script could not be reloaded from the server. The run was not sent.";

export interface ScriptSources {
  /** Per-row unsaved drafts. */
  drafts: Record<string, string>;
  /** Per-row text the Studio already holds for a stored script. */
  saved: Record<string, string>;
  /** Reads a stored script's content. */
  fetchScript: (serviceName: string, filename: string) => Promise<string>;
}

export async function resolveScriptContent(
  sources: ScriptSources,
  rowId: string,
): Promise<string> {
  const draft = sources.drafts[rowId];
  if (draft != null) return draft;
  const saved = sources.saved[rowId];
  if (saved != null) return saved;
  const target = parseResourceId(rowId);
  if (!target) throw new Error("The script could not be loaded");
  try {
    return await sources.fetchScript(target.serviceName, target.resourceName);
  } catch {
    throw new Error(SCRIPT_RELOAD_FAILED);
  }
}

/** The staged rows' scripts in stage order. */
export function stagedScriptContents(
  sources: ScriptSources,
  rowIds: string[],
): Promise<string[]> {
  return Promise.all(rowIds.map((id) => resolveScriptContent(sources, id)));
}
